// Taşeron portalı — personel çekmecesinde KİŞİNİN analiz durumu (GL5 R1). Tepsi (`DocumentAnalysisTray`,
// `--z-dropdown`) çekmece panelinin (`--z-drawer`) altında kalır ve Radix çekmece dışını inert yapar;
// yükleme `Tamam`la kapanınca çekmece AÇIK kalır (araç çekmecesiyle aynı), o yüzden kişinin analizi
// kişinin kendi yüzeyinde görünür. Hiçbir şey HATIRLANMAZ, her şey TÜRETİLİR (`StrandedAnalysesStrip`
// kuralı): sağlayıcı bağlamı yalnız OKUNUR (tepsinin okuduğu gibi), sunucu durumu `useDocumentAnalysis`
// (tepsinin yoklamasıyla AYNI sorgu girdisi) ve `PendingAnalysesStrip`'in liste sorgusudur.
//
// Yuva sırası:
//   1. Başlamadan önce — bu çekmecenin açtığı parti (`uploadedHere`; bağlam partinin kişisini
//      taşımaz) yükleniyor / başlatılıyor / slot bekliyor. Başlatma GERÇEKTEN başarısızsa tepsinin
//      metni (yeniden dene düğmesi tepside kalır, çekmece kapanınca erişilir).
//   2. Sürüyor — sağlayıcının `analysisUuid`'i bu kişiye (`subjectUserUuid`) çözülür ve Queued/Running.
//   3. Onayınızı bekliyor — `PendingAnalysesStrip`, bu kişiye kapsanmış.
//   4. `children` — çağıranın aksayan analiz şeridi; analiz başarısız biterse sonuç ORADA görünür,
//      bu yüzden aynı canlı bölgede duyurulur.
// Bu kişinin analizi terminal GÖRÜLDÜĞÜ ilk an (canlı görülmüş olsun ya da olmasın — ilk yoklama zaten
// terminal dönebilir, çekmece analiz bitince 30 sn'lik önbellekle yeniden açılabilir) bekleyen/aksayan
// listeler analiz başına BİR KEZ geçersiz kılınır (bugün başka hiçbir şey bunları analiz bitince
// tazelemez); parti bu çekmecedense `onSettled` bayrağı indirir.
//
// Erişilebilirlik: TEK kalıcı canlı bölge (`aria-live="polite"`) belge bölümüyle birlikte monte olur,
// böylece İLK değişiklik de duyurulur (`display:none` bir canlı bölge erişilebilirlik ağacında yoktur;
// boşken yalnız aralığı düşer, gizlenmez). `<output>` DEĞİL: içerik modeli yalnız "phrasing"dir, burada
// kart/liste/bağlantı/düğme var. `aria-atomic="false"`: faz değişince yalnız değişen metin okunur,
// şeritlerin tamamı değil. Yalnız faz sınırları duyurulur — yüzde ASLA (tepsiyle aynı kural).

import { Card, resolveApiErrorMessage } from '@isgportal/shared';
import { useQueryClient } from '@tanstack/react-query';
import { CircleAlert, Loader2, Upload } from 'lucide-react';
import { type ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDocumentAnalysisQueue } from '../../lib/DocumentAnalysisProvider';
import { supplierContractorKeys } from '../../lib/supplierContractorKeys';
import { useDocumentAnalysis } from '../../lib/useDocumentAnalysis';
import { PendingAnalysesStrip } from '../PendingAnalysesStrip';

export interface PersonnelAnalysisStatusProps {
  companyId: number | null;
  /** `SupplierUser.Uuid` — analizin `subjectUserUuid`'iyle karşılaştırılır. */
  userUuid: string;
  /** Sağlayıcının son partisini BU çekmece açtı (bağlam partinin kişisini taşımaz). */
  uploadedHere: boolean;
  /** 2. maddedeki analiz terminal oldu — çağıran `uploadedHere`'i indirir. */
  onSettled: () => void;
  /** Aynı canlı bölgede, bekleyen şeridin ALTINDA (aksayan analiz şeridi). */
  children?: ReactNode;
}

type Phase =
  | { kind: 'uploading'; done: number; total: number }
  | { kind: 'starting' | 'queued' | 'running' }
  | { kind: 'start-failed'; message: string };

export function PersonnelAnalysisStatus({
  companyId,
  userUuid,
  uploadedHere,
  onSettled,
  children,
}: PersonnelAnalysisStatusProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { queue, analysisUuid, starting, waitingForSlot, startError } = useDocumentAnalysisQueue();
  const polled = useDocumentAnalysis(analysisUuid);
  const analysis = polled.analysis?.analysis;
  const own = analysis !== undefined && analysis.subjectUserUuid === userUuid ? analysis : null;
  const live = own !== null && (own.status === 'Queued' || own.status === 'Running');

  // Terminal görülen analiz BİR KEZ işlenir (uuid'e bağlı): listeleri tazele; parti bu çekmecedense
  // bayrağı indir. Yeni parti sağlayıcıyı sıfırlar (`analysisUuid` → null), yani eski terminal analiz
  // yeni partinin bayrağını indiremez — o analiz bu bileşen monte olunca zaten işlenmiştir.
  const terminalUuid = own !== null && !live ? own.uuid : null;
  const handledRef = useRef<string | null>(null);
  useEffect(() => {
    if (terminalUuid === null || handledRef.current === terminalUuid) {
      return;
    }
    handledRef.current = terminalUuid;
    queryClient.invalidateQueries({
      queryKey: supplierContractorKeys.analyses(companyId, 'awaiting-review'),
    });
    queryClient.invalidateQueries({ queryKey: supplierContractorKeys.analyses(companyId, 'all') });
    if (uploadedHere) {
      onSettled();
    }
  }, [terminalUuid, uploadedHere, companyId, queryClient, onSettled]);

  let phase: Phase | null = null;
  const uploading = queue.some((item) => item.status === 'pending' || item.status === 'uploading');
  if (uploadedHere && uploading) {
    phase = {
      kind: 'uploading',
      done: queue.filter((item) => item.status === 'done').length,
      total: queue.length,
    };
  } else if (uploadedHere && starting) {
    phase = { kind: 'starting' };
  } else if (uploadedHere && waitingForSlot) {
    phase = { kind: 'queued' }; // `409` bir kuyruk sinyalidir, hata değil (tepsiyle aynı katlama)
  } else if (live) {
    phase = { kind: own?.status === 'Running' ? 'running' : 'queued' };
  } else if (uploadedHere && analysisUuid !== null && polled.isPending) {
    phase = { kind: 'queued' }; // 202 geldi, ilk durum yanıtı henüz yok — sunucu Queued ile açar
  } else if (uploadedHere && startError !== null && analysisUuid === null) {
    phase = { kind: 'start-failed', message: resolveApiErrorMessage(startError, t) };
  }

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="flex flex-col gap-3 [&:not(:empty)]:mt-3"
    >
      {phase ? (
        <Card className="border-border-strong">
          <div className="flex items-start gap-3 p-4">
            {phase.kind === 'uploading' ? (
              <Upload className="mt-0.5 h-5 w-5 shrink-0 text-muted" aria-hidden="true" />
            ) : phase.kind === 'start-failed' ? (
              <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-fg" aria-hidden="true" />
            ) : (
              <Loader2
                className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-muted motion-reduce:animate-none"
                aria-hidden="true"
              />
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium text-fg">
                {phase.kind === 'uploading'
                  ? t('analysis.tray.uploading', { done: phase.done, total: phase.total })
                  : phase.kind === 'start-failed'
                    ? t('analysis.tray.startFailedTitle')
                    : t(`analysis.tray.${phase.kind}`)}
              </p>
              <p className="mt-0.5 text-xs text-muted">
                {phase.kind === 'start-failed' ? phase.message : t('personnelDrawer.analysis.hint')}
              </p>
            </div>
          </div>
        </Card>
      ) : null}
      <PendingAnalysesStrip scope={{ kind: 'personnel', userUuid }} />
      {children}
    </div>
  );
}
